// hardcoded-ui-text: allow — these messages fill the thrown Error; the screens that catch them
// render their own copy. Verified at the three call sites.
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePlatformAdmin } from './PlatformAdminContext';
import { toast } from 'sonner';
import i18n from '@/i18n';

interface ImpersonatedUser {
  auth_user_id: string;
  tenant_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface TenantUser {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string;
  role: string;
  phone_number?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

interface UserImpersonationContextType {
  impersonatedUser: ImpersonatedUser | null;
  tenantUsers: TenantUser[];
  loading: boolean;
  error: string | null;
  startImpersonation: (userAuthId: string, tenantId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  fetchTenantUsers: (tenantSchema: string) => Promise<void>;
}

const UserImpersonationContext = createContext<UserImpersonationContextType | undefined>(undefined);

interface UserImpersonationProviderProps {
  children: React.ReactNode;
}

export function UserImpersonationProvider({ children }: UserImpersonationProviderProps) {
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userCache, setUserCache] = useState<Record<string, { users: TenantUser[]; timestamp: number }>>({});
  const { isPlatformAdmin } = usePlatformAdmin();
  
  // Track pending requests to prevent duplicates
  const pendingRequests = useRef<Set<string>>(new Set());

  // Check for existing impersonation on mount
  useEffect(() => {
    if (isPlatformAdmin) {
      checkImpersonationStatus();
    }
  }, [isPlatformAdmin]);

  const checkImpersonationStatus = async () => {
    if (!isPlatformAdmin) return;
    
    try {
      const { data, error } = await supabase.rpc('get_impersonated_user_context');
      
      if (error) {
        console.error('Error checking impersonation status:', error);
        return;
      }
      
      if (data) {
        setImpersonatedUser(data as unknown as ImpersonatedUser);
      }
    } catch (err) {
      console.error('Error checking impersonation status:', err);
    }
  };

  const startImpersonation = async (userAuthId: string, tenantId: string) => {
    if (!isPlatformAdmin) {
      throw new Error('Only platform admins can impersonate users');
    }

    console.log('🎭 Starting impersonation...', { userAuthId, tenantId });

    try {
      setLoading(true);
      setError(null);

      // Find user info for better logging
      const userInfo = tenantUsers.find(u => u.auth_user_id === userAuthId);
      console.log('🔍 User info found:', userInfo);

      const { error } = await supabase.rpc('set_impersonated_user_context', {
        p_user_auth_id: userAuthId,
        p_tenant_id: tenantId
      });

      console.log('📡 Set impersonation RPC result:', { error });

      if (error) {
        console.error('❌ Set impersonation error:', error);
        throw error;
      }

      // Get the updated impersonation context
      const { data, error: contextError } = await supabase.rpc('get_impersonated_user_context');
      
      console.log('📡 Get impersonation context result:', { data, error: contextError });

      if (contextError) {
        console.error('❌ Get context error:', contextError);
        throw contextError;
      }

      if (data) {
        console.log('✅ Setting impersonated user:', data);
        const impersonatedUserData = data as unknown as ImpersonatedUser;
        setImpersonatedUser(impersonatedUserData);
        
        const displayName = userInfo?.full_name || userInfo?.email || impersonatedUserData.email || 'User';
        console.log('🎉 Impersonation successful, showing toast for:', displayName);
        toast.success(i18n.t('platformAdmin:impersonation.toastStarted', { user: displayName }));
      } else {
        console.warn('⚠️ No impersonation data returned from context');
        toast.error(i18n.t('platformAdmin:impersonation.toastMissing'));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start impersonation';
      console.error('💥 Impersonation failed:', err);
      setError(errorMessage);
      toast.error(i18n.t('platformAdmin:impersonation.toastFailed', { error: errorMessage }));
      throw err;
    } finally {
      setLoading(false);
      console.log('🏁 Impersonation attempt completed');
    }
  };

  const stopImpersonation = async () => {
    if (!isPlatformAdmin) {
      throw new Error('Only platform admins can stop impersonation');
    }

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase.rpc('clear_impersonated_user_context');

      if (error) {
        throw error;
      }

      setImpersonatedUser(null);
      toast.success(i18n.t('platformAdmin:impersonation.toastStopped'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop impersonation';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantUsers = async (tenantSchema: string) => {
    if (!isPlatformAdmin) return;

    // Check cache first (5 minute cache)
    const cached = userCache[tenantSchema];
    const cacheAge = Date.now() - (cached?.timestamp || 0);
    if (cached && cacheAge < 5 * 60 * 1000) {
      setTenantUsers(cached.users);
      return;
    }

    // Prevent duplicate requests
    if (pendingRequests.current.has(tenantSchema)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      pendingRequests.current.add(tenantSchema);

      // Add retry logic with exponential backoff
      let retries = 0;
      const maxRetries = 3;
      let lastError: Error | null = null;

      while (retries < maxRetries) {
        try {
          const { data, error } = await supabase.rpc('get_tenant_users', {
            tenant_schema: tenantSchema,
            limit_count: 100
          });

          if (error) {
            throw error;
          }

          const users = data || [];
          setTenantUsers(users);
          
          // Update cache
          setUserCache(prev => ({
            ...prev,
            [tenantSchema]: { users, timestamp: Date.now() }
          }));
          
          return; // Success, exit retry loop
        } catch (err) {
          lastError = err as Error;
          retries++;
          
          if (retries < maxRetries) {
            // Wait before retry with exponential backoff
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
          }
        }
      }

      // If we get here, all retries failed
      throw lastError;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tenant users';
      setError(errorMessage);
      console.error('Error fetching tenant users after retries:', err);
      setTenantUsers([]); // Clear users on error
    } finally {
      setLoading(false);
      pendingRequests.current.delete(tenantSchema);
    }
  };

  const contextValue: UserImpersonationContextType = {
    impersonatedUser,
    tenantUsers,
    loading,
    error,
    startImpersonation,
    stopImpersonation,
    fetchTenantUsers
  };

  return (
    <UserImpersonationContext.Provider value={contextValue}>
      {children}
    </UserImpersonationContext.Provider>
  );
}

export function useUserImpersonation(): UserImpersonationContextType {
  const context = useContext(UserImpersonationContext);
  if (context === undefined) {
    throw new Error('useUserImpersonation must be used within a UserImpersonationProvider');
  }
  return context;
}